import { AccountSelector } from "@/components/AccountSelector";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Switch } from "@/app/shared/ui/switch";
import { Textarea } from "@groot/ui/textarea";
import { useAccounts } from "@/hooks/api";
import { useBill, useCreateBill, useUpdateBill } from "@/hooks/api/useBills";
import { type BillFormData, BillTrackingMode, BillingCycleType, ReminderType } from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, Save } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function BillForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { data: bill, isLoading } = useBill(Number(id));
  const { data: accounts = [] } = useAccounts();
  const createBill = useCreateBill();
  const updateBill = useUpdateBill();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<BillFormData>({
    defaultValues: {
      name: "",
      notes: "",
      amount: 0,
      billingDate: dayjs().format("YYYY-MM-DDTHH:mm"),
      endDate: "",
      billingCycleType: BillingCycleType.MONTH,
      billingCycleValue: 1,
      accountId: undefined,
      trackingMode: BillTrackingMode.MANUAL,
      receiveReminders: true,
      reminderType: ReminderType.SAME_DAY,
      reminderDays: 3,
      isActive: true,
    },
  });

  const reminderType = watch("reminderType");
  const receiveReminders = watch("receiveReminders");

  useEffect(() => {
    if (bill) {
      reset({
        name: bill.name,
        notes: bill.notes || "",
        amount: Number(bill.amount),
        billingDate: dayjs(bill.billingDate).format("YYYY-MM-DDTHH:mm"),
        endDate: bill.endDate ? dayjs(bill.endDate).format("YYYY-MM-DDTHH:mm") : "",
        billingCycleType: bill.billingCycleType,
        billingCycleValue: bill.billingCycleValue,
        accountId: bill.accountId || undefined,
        trackingMode: bill.trackingMode,
        receiveReminders: bill.receiveReminders,
        reminderType: bill.reminderType,
        reminderDays: bill.reminderDays || 3,
        isActive: bill.isActive,
      });
    }
  }, [bill, reset]);

  const onSubmit = async (data: BillFormData) => {
    try {
      // Convert datetime-local format to ISO format for API
      const payload = {
        ...data,
        billingDate: dayjs(data.billingDate).toISOString(),
        endDate: data.endDate ? dayjs(data.endDate).toISOString() : undefined,
        // Send null if accountId is 0 or undefined/null to clear the field
        accountId: !data.accountId || data.accountId === 0 ? null : data.accountId,
      };

      if (isEditMode) {
        await updateBill.mutateAsync({
          id: Number(id),
          data: payload,
        });
      } else {
        await createBill.mutateAsync(payload);
      }
      // Redirect to detail page for edit, or listing for new
      if (isEditMode) {
        navigate(`/bills/${id}`);
      } else {
        navigate("/bills");
      }
    } catch (error) {
      // Error toast is handled by the mutation hooks
      console.error("Failed to save bill:", error);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <Page maxWidth="3xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page maxWidth="3xl">
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => (isEditMode ? navigate(`/bills/${id}`) : navigate("/bills"))}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Bills
      </Button>
      <PageHeader
        title={isEditMode ? "Edit Bill" : "Add New Bill"}
        description={
          isEditMode ? "Update bill details and schedule" : "Create a new recurring bill"
        }
        className="mb-8"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Info */}
        {/* Basic Info */}
        <div className="border border-border rounded-lg p-6">
          <h2 className="text-base font-semibold text-foreground mb-6">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label htmlFor="name" className="text-sm font-medium text-foreground/80">
                Bill Name *
              </Label>
              <Input
                id="name"
                {...register("name", { required: "Name is required" })}
                placeholder="e.g., Netflix Subscription"
                className="mt-1.5"
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="amount" className="text-sm font-medium text-foreground/80">
                Amount *
              </Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  ₹
                </span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  {...register("amount", {
                    required: "Amount is required",
                    min: { value: 0.01, message: "Amount must be positive" },
                    valueAsNumber: true,
                  })}
                  placeholder="0.00"
                  className="pl-7"
                />
              </div>
              {errors.amount && (
                <p className="text-sm text-destructive mt-1">{errors.amount.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="notes" className="text-sm font-medium text-foreground/80">
                Notes
              </Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Additional details about this bill..."
                rows={3}
                className="mt-1.5 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Billing Schedule */}
        <div className="border border-border rounded-lg p-6">
          <h2 className="text-base font-semibold text-foreground mb-6">Billing Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="billingDate" className="text-sm font-medium text-foreground/80">
                First Billing Date *
              </Label>
              <Input
                id="billingDate"
                type="datetime-local"
                {...register("billingDate", {
                  required: "Billing date is required",
                })}
                className="mt-1.5"
              />
              {errors.billingDate && (
                <p className="text-sm text-destructive mt-1">{errors.billingDate.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="endDate" className="text-sm font-medium text-foreground/80">
                End Date (Optional)
              </Label>
              <Input
                id="endDate"
                type="datetime-local"
                {...register("endDate")}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="billingCycleType" className="text-sm font-medium text-foreground/80">
                Frequency *
              </Label>
              <Select
                value={watch("billingCycleType")}
                onValueChange={(value) => setValue("billingCycleType", value as BillingCycleType)}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BillingCycleType.DAY}>Daily</SelectItem>
                  <SelectItem value={BillingCycleType.WEEK}>Weekly</SelectItem>
                  <SelectItem value={BillingCycleType.MONTH}>Monthly</SelectItem>
                  <SelectItem value={BillingCycleType.YEAR}>Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="billingCycleValue" className="text-sm font-medium text-foreground/80">
                Every X Period(s) *
              </Label>
              <Input
                id="billingCycleValue"
                type="number"
                min="1"
                {...register("billingCycleValue", {
                  required: "Cycle value is required",
                  min: { value: 1, message: "Must be at least 1" },
                  valueAsNumber: true,
                })}
                className="mt-1.5"
              />
              {errors.billingCycleValue && (
                <p className="text-sm text-destructive mt-1">{errors.billingCycleValue.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="border border-border rounded-lg p-6">
          <h2 className="text-base font-semibold text-foreground mb-6">Payment Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="accountId" className="text-sm font-medium text-foreground/80">
                Payment Account (Optional)
              </Label>
              <AccountSelector
                accounts={accounts}
                value={watch("accountId") === undefined ? "none" : watch("accountId")?.toString()}
                onValueChange={(value) =>
                  setValue("accountId", value === "none" ? undefined : Number(value))
                }
                placeholder="Select an account"
                showAllOption
                allOptionLabel="None"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="trackingMode" className="text-sm font-medium text-foreground/80">
                Bill Tracking
              </Label>
              <Select
                value={watch("trackingMode")}
                onValueChange={(value) => setValue("trackingMode", value as BillTrackingMode)}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BillTrackingMode.MANUAL}>
                    Manual (Mark as paid manually)
                  </SelectItem>
                  <SelectItem value={BillTrackingMode.AUTO}>
                    Auto (Mark as paid on billing date)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Reminders & Status */}
        <div className="border border-border rounded-lg p-6">
          <h2 className="text-base font-semibold text-foreground mb-6">Notifications & Status</h2>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-2 h-10 mt-6 md:mt-0">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="receiveReminders"
                    checked={receiveReminders}
                    onCheckedChange={(checked) => setValue("receiveReminders", checked)}
                  />
                  <Label
                    htmlFor="receiveReminders"
                    className="cursor-pointer text-sm font-medium text-foreground/80"
                  >
                    Receive payment reminders
                  </Label>
                </div>
              </div>

              <div className="flex items-center space-x-2 h-10 mt-6 md:mt-0">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={watch("isActive")}
                    onCheckedChange={(checked) => setValue("isActive", checked)}
                  />
                  <Label
                    htmlFor="isActive"
                    className="cursor-pointer text-sm font-medium text-foreground/80"
                  >
                    Bill is active
                  </Label>
                </div>
              </div>
            </div>

            {receiveReminders && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                <div>
                  <Label htmlFor="reminderType" className="text-sm font-medium text-foreground/80">
                    Reminder Timing
                  </Label>
                  <Select
                    value={watch("reminderType")}
                    onValueChange={(value) => setValue("reminderType", value as ReminderType)}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ReminderType.SAME_DAY}>On the billing date</SelectItem>
                      <SelectItem value={ReminderType.DAYS_BEFORE}>X days before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {reminderType === ReminderType.DAYS_BEFORE && (
                  <div>
                    <Label
                      htmlFor="reminderDays"
                      className="text-sm font-medium text-foreground/80"
                    >
                      Days Before *
                    </Label>
                    <Input
                      id="reminderDays"
                      type="number"
                      min="1"
                      max="30"
                      {...register("reminderDays", {
                        required:
                          reminderType === ReminderType.DAYS_BEFORE
                            ? "Days before is required"
                            : false,
                        min: { value: 1, message: "Must be at least 1 day" },
                        max: { value: 30, message: "Cannot exceed 30 days" },
                        valueAsNumber: true,
                      })}
                      className="mt-1.5"
                    />
                    {errors.reminderDays && (
                      <p className="text-sm text-destructive mt-1">{errors.reminderDays.message}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-6 border-t border-border">
          <Button type="submit" disabled={isSubmitting}>
            <Save className="size-4" />
            {isSubmitting ? "Saving..." : isEditMode ? "Update Bill" : "Create Bill"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/bills")}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Page>
  );
}
